import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n, pickLocalized } from "@/lib/i18n";
import { formatEUR, formatKgPrice } from "@/lib/format";
import { useCart } from "@/lib/cart";
import { Seo } from "@/components/Seo";
import { MaskedLine, FadeUp, StaggerWords } from "@/components/Motion";
import { ArrowUpRight, Instagram } from "lucide-react";
import OriginSection from "@/components/OriginSection";
import UnikatSection from "@/components/UnikatSection";
import SensorySection from "@/components/SensorySection";
import { TestimonialMarquee } from "@/components/TestimonialMarquee";
import BeyondNature from "@/components/BeyondNature";
import PatternDivider from "@/components/PatternDivider";

export default function Home() {
  const { t, lang } = useI18n();
  const [products, setProducts] = useState([]);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 800], [0, 200]);
  const heroScale = useTransform(scrollY, [0, 800], [1, 1.15]);

  useEffect(() => { api.get("/products?featured=true").then((r) => setProducts(r.data)); }, []);

  return (
    <div data-testid="home-page">
      <Seo title={`Madubau Dolomiti — ${t.hero.sub}`} description={t.hero.body} path="/" />
      {/* HERO */}
      <section className="relative h-[100vh] overflow-hidden bg-black">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <img
            src="https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/03kpugbk_WhatsApp%20Image%202026-07-27%20at%2016.49.20.jpeg"
            alt="Dolomites at golden hour"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black" />
          <div className="absolute inset-0 hero-glow" />
        </motion.div>

        {/* Left-anchored dark scrim for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/45 to-transparent pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none" />

        <div className="relative h-full max-w-[1600px] mx-auto px-6 lg:px-12 flex flex-col justify-center pt-24 pb-24 z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.4 }}
            className="mb-6"
          >
            <h2 className="font-display font-light text-[7.5vw] sm:text-[6vw] lg:text-[4.5vw] xl:text-[4vw] tracking-[0.06em] leading-[0.98] text-gold max-w-5xl">
              {t.hero.sub.toUpperCase()}
            </h2>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1.05 }}
            className="text-cream/80 font-body text-base sm:text-lg max-w-xl mb-10"
          >
            {t.hero.body}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1.2 }}
            className="flex flex-wrap items-center gap-6"
          >
            <Link
              to="/shop"
              data-testid="hero-cta-shop"
              className="group inline-flex items-center gap-3 bg-gold text-black px-9 py-4 font-display text-[11px] tracking-[0.35em] hover:bg-gold-light transition-colors gold-shine"
              style={{ background: "#C9A24C" }}
            >
              {t.hero.cta.toUpperCase()}
              <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
            </Link>
            <Link
              to="/story"
              data-testid="hero-cta-story"
              className="inline-flex items-center gap-2 text-cream/85 hover:text-gold font-display text-[11px] tracking-[0.3em] link-underline"
            >
              {t.hero.cta2} →
            </Link>
          </motion.div>
        </div>

        <div className="absolute bottom-8 right-6 lg:right-12 font-display text-[9px] tracking-[0.4em] text-cream/40 hidden sm:block z-10">
          46°26′N · 11°45′E
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-white/5 py-6 bg-[#0A0A0A] marquee-fade" data-testid="editorial-marquee">
        <Marquee speed={35} gradient={false}>
          {Array(4).fill(0).map((_, i) => (
            <span key={i} className="font-display text-sm tracking-[0.4em] text-gold mx-8">
              {t.marquee}
              <span className="text-gold mx-6">✦</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="py-32 lg:py-40" data-testid="featured-section">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <FadeUp className="flex items-end justify-between mb-20 flex-wrap gap-6">
            <div>
              <h2 className="font-editorial italic font-light text-5xl lg:text-6xl text-cream">
                {t.featured.title}
              </h2>
            </div>
            <Link to="/shop" data-testid="all-products-link" className="font-display text-[11px] tracking-[0.3em] text-gold hover:text-gold-light link-underline">
              → View all
            </Link>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {products.map((p, i) => (
              <FadeUp key={p.id} delay={i * 0.1}>
                <ProductCard product={p} />
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* BEYOND NATURE — Icon Section */}
      <BeyondNature />

      {/* ORIGIN — Unsere Herkunft */}
      <OriginSection />

      {/* UNIKAT — Ein Unikat */}
      <UnikatSection />

      {/* ANATOMY — removed per user request */}

      {/* SENSORY — Sensorisches Profil */}
      <SensorySection />

      {/* BEWERTUNGEN — Laufband DE/EN/IT */}
      <TestimonialMarquee />

      {/* INSTAGRAM */}
      <section className="py-32 lg:py-40 border-t border-white/5" data-testid="instagram-section">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <FadeUp className="text-center mb-16">
            <div className="font-display text-[10px] tracking-[0.4em] text-gold mb-4">/ FIELD NOTES</div>
            <h2 className="font-editorial italic font-light text-4xl lg:text-5xl text-cream mb-6">
              Follow the harvest
            </h2>
            <a
              href="https://www.instagram.com/madubau_honey/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ig-follow-link"
              className="inline-flex items-center gap-2 text-gold hover:text-gold-light font-display text-[11px] tracking-[0.3em] link-underline"
            >
              <Instagram className="w-4 h-4" /> @madubau_honey
            </a>
          </FadeUp>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              "https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/2fjt5cw2_WhatsApp%20Image%202026-07-27%20at%2016.47.09%281%29.webp",
              "https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/rrkqpwlp_WhatsApp%20Image%202026-07-27%20at%2012.47.09.jpeg",
              "https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/o11gaawi_WhatsApp%20Image%202026-07-27%20at%2012.47.09%20%286%29.jpeg",
              "https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/kv469xpj_WhatsApp%20Image%202026-07-27%20at%2012.47.09%20%281%29.jpeg",
            ].map((src, i) => (
              <FadeUp key={i} delay={i * 0.08}>
                <a href="https://www.instagram.com/madubau_honey/" target="_blank" rel="noopener noreferrer" className="block aspect-square overflow-hidden group">
                  <img src={src} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                </a>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export function ProductCard({ product }) {
  const { lang, t } = useI18n();
  const { add } = useCart();
  const name = pickLocalized(product, lang, "name");
  const tagline = pickLocalized(product, lang, "tagline");

  return (
    <div className="product-card group" data-testid={`product-card-${product.slug}`}>
      <Link to={`/product/${product.slug}`} className="block">
        <div className="relative aspect-[4/5] overflow-hidden clip-frame bg-surface-2 mb-6">
          <img src={product.image_url} alt={name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
          <div className="absolute top-4 left-4 font-display text-[11px] tracking-[0.25em] text-gold bg-black/70 backdrop-blur px-3 py-1.5" style={{ textTransform: "none" }} data-testid={`weight-badge-${product.slug}`}>
            {product.weight_g} g ℮
          </div>
          {product.limited && (
            <div className="absolute top-4 right-4 font-display text-[9px] tracking-[0.3em] text-black bg-gold px-3 py-1.5 clip-tag" data-testid={`limited-badge-${product.slug}`}>
              · {t.product.limited} ·
            </div>
          )}
        </div>
        <div className="font-editorial italic text-xs text-gold/80 mb-2">— {product.origin}</div>
        <h3 className="font-display text-sm tracking-[0.25em] text-cream mb-2">{name}</h3>
        <p className="text-cream/50 text-sm font-body italic mb-4 line-clamp-1">{tagline}</p>
        <div className="flex items-center justify-between">
          <div>
            <span className="font-editorial text-xl text-gold">{formatEUR(product.price_eur, lang)}</span>
            <span className="block text-[10px] text-cream/40 font-body mt-0.5" data-testid={`base-price-${product.slug}`}>{formatKgPrice(product.price_eur, product.weight_g, lang)}</span>
          </div>
          <button
            onClick={(e) => { e.preventDefault(); add(product); }}
            data-testid={`add-to-cart-${product.slug}`}
            className="font-display text-[10px] tracking-[0.3em] text-cream/80 hover:text-gold link-underline"
          >
            → {t.product.add}
          </button>
        </div>
      </Link>
    </div>
  );
}
